import CircularGauge, {
  Range,
  RangeContainer,
  Scale,
  Size,
  ValueIndicator
} from 'devextreme-react/circular-gauge';
import type { TimeOffDashboardSummary } from '../../../api/timeOff';

interface TimeOffSyncHealthCardProps {
  summary: TimeOffDashboardSummary;
}

function formatDate(value: string | null): string {
  if (!value) return 'Never';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(new Date(value));
}

function health(summary: TimeOffDashboardSummary): { score: number; label: string; detail: string } {
  const sync = summary.lastSyncStatus;
  if (sync.lastErrors > 0) {
    return { score: 20, label: 'Needs attention', detail: `${sync.lastErrors} error(s) on the last sync` };
  }
  if (!sync.lastFinishedAt) {
    return { score: 40, label: 'No completed sync', detail: 'Waiting for the first successful run' };
  }
  if (summary.dirtyLinks > 0) {
    return { score: 68, label: 'Pending sync', detail: `${summary.dirtyLinks} calendar update(s) queued` };
  }

  const finished = new Date(sync.lastFinishedAt).getTime();
  const staleMs = 2 * 60 * 60 * 1000;
  if (Number.isFinite(finished) && Date.now() - finished > staleMs) {
    return { score: 78, label: 'Quiet', detail: 'No pending work, but sync has not run recently' };
  }

  return { score: 96, label: 'Healthy', detail: 'No calendar updates waiting' };
}

function formatProcessedCount(processed: number, hasCompletedRun: boolean): string {
  if (!hasCompletedRun) return 'None';
  if (processed === 0) return '0 items';
  return `${processed} item${processed === 1 ? '' : 's'}`;
}

export function TimeOffSyncHealthCard({ summary }: TimeOffSyncHealthCardProps) {
  const state = health(summary);
  const sync = summary.lastSyncStatus;
  const hasCompletedRun = !!(sync.lastFinishedAt ?? sync.lastStartedAt);

  return (
    <section className="dashboard-card dashboard-card--sync">
      <div className="dashboard-card__header">
        <div>
          <h3 className="dashboard-card__title">Calendar Sync Health</h3>
          <p>Calendar projection status</p>
        </div>
      </div>

      <div className="dashboard-gauge-wrap">
        <CircularGauge value={state.score}>
          <Size height={145} />
          <Scale startValue={0} endValue={100} tickInterval={25} />
          <RangeContainer>
            <Range startValue={0} endValue={50} />
            <Range startValue={50} endValue={80} />
            <Range startValue={80} endValue={100} />
          </RangeContainer>
          <ValueIndicator type="triangleNeedle" />
        </CircularGauge>
      </div>

      <div className="dashboard-sync-status">
        <strong>{state.label}</strong>
        <span>{state.detail}</span>
      </div>

      <dl className="dashboard-detail-list">
        <div>
          <dt>Last sync</dt>
          <dd>{formatDate(sync.lastFinishedAt ?? sync.lastStartedAt)}</dd>
        </div>
        <div>
          <dt>Next run</dt>
          <dd>{formatDate(sync.nextFireTimeUtc)}</dd>
        </div>
        <div>
          <dt>Last run processed</dt>
          <dd>{formatProcessedCount(sync.lastProcessed, hasCompletedRun)}</dd>
        </div>
      </dl>
    </section>
  );
}
